import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useTranslation } from '@/hooks/useTranslation'
import { Footer } from '@/components/landing/Footer'

export function LegalPageLayout({
	title,
	lastUpdated,
	children,
}: {
	title: string
	lastUpdated: string
	children: ReactNode
}) {
	const { t } = useTranslation()

	return (
		<>
			<main className="min-h-screen w-full bg-gradient-to-b from-gray-50 to-white">
				<div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
					<div className="mb-10">
						<Link
							to="/"
							className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors mb-6 group"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
								fill="currentColor"
								className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform"
							>
								<path
									fillRule="evenodd"
									d="M15 10a.75.75 0 01-.75.75H7.612l2.158 1.96a.75.75 0 11-1.04 1.08l-3.5-3.25a.75.75 0 010-1.08l3.5-3.25a.75.75 0 111.04 1.08L7.612 9.25h6.638A.75.75 0 0115 10z"
									clipRule="evenodd"
								/>
							</svg>
							{t('legal.home')}
						</Link>
						<h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-3">
							{title}
						</h1>
						<p className="text-gray-500">{lastUpdated}</p>
					</div>
					<div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
						<article className="prose prose-gray max-w-none p-8">
							<div className="space-y-8">{children}</div>
						</article>
					</div>
				</div>
			</main>
			<Footer />
		</>
	)
}

export function LegalSection({
	title,
	children,
}: {
	title: string
	children: ReactNode
}) {
	return (
		<section>
			<h2 className="text-2xl font-semibold text-gray-900 mb-4">{title}</h2>
			{children}
		</section>
	)
}

export function LegalP({ children }: { children: ReactNode }) {
	return <p className="text-gray-600 leading-relaxed mt-4 first:mt-0">{children}</p>
}

export function LegalH3({ children }: { children: ReactNode }) {
	return <h3 className="text-xl font-medium text-gray-800 mt-4">{children}</h3>
}

export function LegalList({ items }: { items: string[] }) {
	return (
		<ul className="list-disc pl-5 text-gray-600 space-y-2 mt-2">
			{items.map((item) => (
				<li key={item}>{item}</li>
			))}
		</ul>
	)
}

export function useLegalList(key: string): string[] {
	const { t } = useTranslation()
	const value = t(key, { returnObjects: true })
	return Array.isArray(value) ? (value as string[]) : []
}

export function useLegalUpdatedDate() {
	const { i18n } = useTranslation()
	return i18n.language?.startsWith('fr')
		? '15 septembre 2026'
		: '15 September 2026'
}
